import * as SentryReact from '@sentry/react';
import * as SentryNode from '@sentry/node';

class SentrySDK {
  constructor() {
    this.environment = this.detectEnvironment();
    this.initialized = false
  }

  detectEnvironment() {
    return typeof window !== 'undefined' && typeof document !== 'undefined'
      ? 'browser'
      : 'node';
  }

  init(options) {
    if (this.environment === 'browser') {
      this.initBrowser(options);
    } else {
      this.initNode(options);
    }
    this.initialized = true
    return this
  }

  initBrowser(options) {
    const integrations = [
      SentryReact.browserTracingIntegration(),
      SentryReact.replayIntegration(),
    ];

    // Add React Router integration if provided
    if (options.reactRouter) {
      integrations.push(
        SentryReact.reactRouterV6BrowserTracingIntegration({
          useEffect: options.reactRouter.useEffect,
          useLocation: options.reactRouter.useLocation,
          useNavigationType: options.reactRouter.useNavigationType,
          createRoutesFromChildren: options.reactRouter.createRoutesFromChildren,
          matchRoutes: options.reactRouter.matchRoutes,
        })
      );
    }

    SentryReact.init({
      dsn: options.dsn,
      integrations,
      autoSessionTracking: true,
      tracesSampleRate: options.tracesSampleRate || 0.01,
      replaysSessionSampleRate: options.replaysSessionSampleRate || 0.01,
      replaysOnErrorSampleRate: options.replaysOnErrorSampleRate || 0.01,
      environment: options.environment,
    });

    window.onerror = (message, source, lineno, colno, error) => {
      SentryReact.captureException(error || new Error(message));
      // Return true if you want to prevent the default browser error handling
      return false;
    };

    // Catch unhandled promise rejections
    window.addEventListener('unhandledrejection', (event) => {
      SentryReact.captureException(event.reason);
    });
  }

  initNode(options) {
    const integrations = [
      SentryNode.httpIntegration({ tracing: true }),
      SentryNode.modulesIntegration
    ];

    // Add Express integration if app is provided
    if (options.expressApp) {
      integrations.push(
        new SentryNode.Integrations.Express({
          app: options.expressApp,
        })
      );
    }

    SentryNode.init({
      dsn: options.dsn,
      integrations,
      tracesSampleRate: options.tracesSampleRate || 0.01,
      environment: options.environment,
    });
  }

  captureException(exception, context) {
    return this.environment === 'browser'
      ? SentryReact.captureException(exception, context)
      : SentryNode.captureException(exception, context);
  }

  captureMessage(message, context) {
    return this.environment === 'browser'
      ? SentryReact.captureMessage(message, context)
      : SentryNode.captureMessage(message, context);
  }

  setUser(user) {
    this.environment === 'browser'
      ? SentryReact.setUser(user)
      : SentryNode.setUser(user);
  }

  setTag(key, value) {
    this.environment === 'browser'
      ? SentryReact.setTag(key, value)
      : SentryNode.setTag(key, value);
  }
}

export default new SentrySDK();